import axios from 'axios';
import find from 'lodash/find';
import get from 'lodash/get';
import intersection from 'lodash/intersection';

import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Terminal from 'app/src/payments/terminals/Terminal';
import { hasFeature } from 'root/api/admin/features';
import { datadogRum } from 'root/datadog/datadogRum';
import Modules from 'shared_modules/Modules';
import Utility from 'shared_modules/Utility';

// Version doesn't have much context right now with Retail.  Perhaps, once a version is developed we
// can change this to pull from the version source.
const _productVersion = '1.0';

const STRIPE_SDK_FILENAME = ''; // Only specified if using a version different from the official release
const STRIPE_SDK_VERSION = '/terminal/v1/';
const STRIPE_CDN_URL = 'https://js.stripe.com';
const CTS_TOKEN_URL = '/admin/payments/CtsJwt';
const STRIPE_SUPPORTED_COUNTRY_CODES = [
  { countryCode: 'CA', iso3: 'can' },
  { countryCode: 'US', iso3: 'usa' },
  { countryCode: 'GB', iso3: 'gbr' },
  { countryCode: 'AU', iso3: 'aus' },
  { countryCode: 'NL', iso3: 'eur' },
  { countryCode: 'BE', iso3: 'eur' },
  { countryCode: 'IE', iso3: 'eur' },
];

export const UnhandledStripeServiceError = {
  no_established_connection: {
    code: 'no_established_connection',
    status: Terminal.Response.ERROR,
  }, // The command failed because no reader is connected.
  no_active_collect_payment_method_attempt: {
    code: 'no_active_collect_payment_method_attempt',
    status: Terminal.Response.ERROR,
  }, // cancelCollectPaymentMethod can only be called when collectPaymentMethod is in progress.
  no_active_read_reusable_card_attempt: {
    code: 'no_active_read_reusable_card_attempt',
    status: Terminal.Response.ERROR,
  }, // cancelCollectReusableCard can only be called when readReusableCard is in progress.
  canceled: {
    code: 'canceled',
    status: Terminal.Response.CANCELLED,
  }, // The command was canceled from POS
  cancelable_already_completed: {
    code: 'cancelable_already_completed',
    status: Terminal.Response.ERROR,
  }, // Cancelation failed because the operation has already completed.
  cancelable_already_canceled: {
    code: 'cancelable_already_canceled',
    status: Terminal.Response.ERROR,
  }, // Cancelation failed because the operation has already been canceled.
  network_error: {
    code: 'network_error',
    status: Terminal.Response.ERROR,
  }, // An unknown error occurred when communicating with the server or reader over the network. Refer to the error message for more information.
  network_timeout: {
    code: 'network_timeout',
    status: Terminal.Response.ERROR,
  }, // The request timed out when communicating with the server or reader over the network. Make sure both your device and the reader are connected to the network with stable connections.
  already_connected: {
    code: 'already_connected',
    status: Terminal.Response.ERROR,
  }, // connectReader failed because a reader is already connected.
  failed_fetch_connection_token: {
    code: 'failed_fetch_connection_token',
    status: Terminal.Response.ERROR,
  }, // Failed to fetch a connection token. Make sure your connection token handler returns a promise that resolves to the connection token.
  discovery_too_many_readers: {
    code: 'discovery_too_many_readers',
    status: Terminal.Response.ERROR,
  }, // discoverReaders returned too many readers. Use Locations to filter discovered readers by location.
  invalid_reader_version: {
    code: 'invalid_reader_version',
    status: Terminal.Response.ERROR,
  }, // The reader is running an unsupported software version. Please allow the reader to update and try again.
  reader_error: {
    code: 'reader_error',
    status: Terminal.Response.ERROR,
  }, // The reader returned an error while processing the request. Refer to the error message for more information. This is also reached when customer cancels payment on terminal.
  payment_intent_unexpected_state: {
    code: 'payment_intent_unexpected_state',
    status: Terminal.Response.ERROR,
  }, // The reader return an error while processing a payment.
  waiting_for_input: {
    code: 'waiting_for_input',
    status: Terminal.Response.ERROR,
  }, // The reader internal status waiting for the user input.
  // Non-documented API error codes that are not reaching the bank network and by the same time have no webhooks in case of failure.
  card_declined: {
    code: 'card_declined',
    status: Terminal.Response.DECLINED,
  }, // The card is declined for some reason.
  incorrect_number: {
    code: 'incorrect_number',
    status: Terminal.Response.DECLINED,
  }, // The card number is incorrect.
  expired_card: {
    code: 'expired_card',
    status: Terminal.Response.DECLINED,
  }, // The card has expired. Check the expiration date or use a different card.
  invalid_expiry_month: {
    code: 'invalid_expiry_month',
    status: Terminal.Response.DECLINED,
  }, // The card’s expiration month is incorrect. Check the expiration date or use a different card.
  invalid_expiry_year: {
    code: 'invalid_expiry_year',
    status: Terminal.Response.DECLINED,
  }, // The card’s expiration year is incorrect. Check the expiration date or use a different card.
  // Catch all custom error codes
  reader_register_error: {
    code: 'reader_register_error',
    status: Terminal.Response.ERROR,
  },
  configuration_exception: {
    code: 'configuration_exception',
    status: Terminal.Response.ERROR,
  }, // If one of the SDK method fail or the merchant has bad configuration
  service_exception: {
    code: 'service_exception',
    status: Terminal.Response.ERROR,
  }, // If one of the SDK method fail or the merchant has bad configuration
  setup_intent_unexpected_state: {
    code: 'setup_intent_unexpected_state',
    status: Terminal.Response.ERROR,
  }, // Fails on collecting/confirming with a setup Intent
  unknown_code: {
    code: 'unknown_code',
    status: Terminal.Response.UNKNOWN,
  }, // If one of the error returns no error code.
};

export const StripeTerminalPaymentStatus = {
  WAITING_FOR_INPUT: 'waiting_for_input', // Waiting for payment from user
  NOT_READY: 'not_ready',
  READY: 'ready', // Ready to accept a payment
  PROCESSING: 'processing', // Processing Payment
};

export default class StripeTerminalService {
  static instance;

  gatewayConfig;

  currentTerminal;

  currentCollectMethod;

  /**
   * Retrieves the singleton instance of the StripeTerminalService. This is constructed as
   * a singleton because we can only have one instance of the Stripe API active at once.
   */
  static async getInstance() {
    if (StripeTerminalService.instance) return StripeTerminalService.instance;

    const countryCode = get(window.merchantos, 'locale.country_code');
    const currency = get(window.merchantos, 'locale.currency_code');
    const supportedCountryCode = find(STRIPE_SUPPORTED_COUNTRY_CODES, {
      countryCode,
    });

    try {
      if (!currency && typeof currency !== 'string') {
        throw {
          code: UnhandledStripeServiceError.configuration_exception.code,
          message: `Unable to connect to your terminal. This could be caused by an incompatible configuration setting or an unsupported currency set for this account.\n You can update this under Settings > General Options.`,
          reason: `Currency code value is: ${currency}`,
        };
      }
      if (!supportedCountryCode) {
        throw {
          code: UnhandledStripeServiceError.configuration_exception.code,
          message: `Unable to connect to your terminal. This could be caused by an incompatible configuration setting or an unsupported account location.\n You can update this under Settings > General Options.`,
          reason: `Country code value is: ${countryCode}`,
        };
      }

      await Utility.fetchScript(`${STRIPE_CDN_URL}${STRIPE_SDK_VERSION}${STRIPE_SDK_FILENAME}`);

      StripeTerminalService.instance = new StripeTerminalService(supportedCountryCode, currency);

      return StripeTerminalService.instance;
    } catch (err) {
      throw err;
    }
  }

  /**
   * Constructs a StripeTerminalService instance
   *
   * @returns A Promise of the StripeTerminalService
   */
  constructor(supportedCountryCode, currency) {
    this.currency = currency;
    this.supportedCountryCode = supportedCountryCode;
    this._terminalMonitoringInstance = null;
    this._stripeTerminalApi = window.StripeTerminal.create({
      onFetchConnectionToken: this._fetchConnectionToken.bind(this),
      onUnexpectedReaderDisconnect: () => {
        console.info('Terminal Event:', 'Unexpected Terminal Disconnect');
      },
      onConnectionStatusChange: (ev) => {
        console.info('Terminal Event:', ev.status);
      },
      // Allows the cancellation of the collect payment method from the terminal.
      allowCustomerCancel: true,
    });
  }

  setTerminalMonitoringInstance(terminalMonitoringInstance) {
    this._terminalMonitoringInstance = terminalMonitoringInstance;
    this._stripeTerminalApi = terminalMonitoringInstance.defineTerminal(this._stripeTerminalApi);
  }

  /**
   * Begins discovering readers - Step 1
   *
   *
   * @returns a Promise that resolves to an object with the following fields:
   *
   * @return {object[]} discoveredReaders: A list of discovered Reader objects, if the command succeeded
   * @return {error} error: An error, if the command failed.
   */
  async discoverReaders(gatewayParameters) {
    let processor, terminalLocation;

    const { supportedProcessors, processorData } = gatewayParameters || {};
    const supportedCountries = STRIPE_SUPPORTED_COUNTRY_CODES.map(({ iso3 }) => `stripe-${iso3}`);

    if (supportedProcessors) {
      processor = intersection(supportedCountries, JSON.parse(supportedProcessors))[0];
    } else {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to get supportedProcessors',
      };
    }

    if (processorData) {
      terminalLocation = get(JSON.parse(processorData), `${processor}.terminalLocation`, null);
    } else {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to get processorData',
      };
    }

    try {
      const response = await this._stripeTerminalApi.discoverReaders({
        method: null,
        location: terminalLocation,
      });

      return this._handleStripeResponse(response);
    } catch (err) {
      console.error('Stripe Discover Error: ', err);
      datadogRum.addError(err, {
        message: 'Failed to Discover Reader',
        location: terminalLocation,
      });
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to discover readers',
        error: err,
      };
    }
  }

  /**
   * Attempts to connect to the given reader - Step 2
   *
   *
   * @param {object} selectedReader Represent the reader object
   * coming back from the discover reader response
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {object} reader: The connected Reader, if the command succeeded.
   * @return {error} error: An error, if the command failed.
   */
  async connectReader(selectedReader) {
    let response;
    try {
      response = await this._stripeTerminalApi.connectReader(selectedReader);

      if (response.reader) {
        this.currentTerminal = response.reader;
      }
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to connect to a reader',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * Disconnects from the connected reader - Step 2a
   */
  disconnectReader() {
    this._stripeTerminalApi.disconnectReader();
  }

  /**
   * Get the connection status from the reader instance
   */
  getConnectionStatus() {
    return this._stripeTerminalApi.getConnectionStatus();
  }

  /**
   * Get the payment status from the reader instance
   * https://stripe.com/docs/terminal/js-api-reference#get-payment-status
   */
  getPaymentStatus() {
    return this._stripeTerminalApi.getPaymentStatus();
  }

  /**
   * Begins collecting a payment method for a PaymentIntent - Step 3
   *
   *
   * This method takes a single argument, the client_secret field
   * from a PaymentIntent object created on your backend.
   *
   * @param {string} paymentIntentClientSecret field from a PaymentIntent object created
   * by the backend.
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {object} paymentIntent: The updated PaymentIntent object, if the command succeeded
   * @return {error} error: An error, if the command failed.
   */
  async collectPaymentMethod(paymentIntentClientSecret) {
    let response;
    try {
      this.currentCollectMethod = 'collectPaymentMethod';
      response = await this._stripeTerminalApi.collectPaymentMethod(paymentIntentClientSecret);
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to collect the payment method',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * calls the appropriate cancel method based on the current collect method in progress
   */
  async cancelCollect() {
    let response;
    if (Modules.hasModule('payments.clientSide.CancelPayment')) {
      try {
        switch (this.currentCollectMethod) {
          case 'collectPaymentMethod':
            response = await this._stripeTerminalApi.cancelCollectPaymentMethod();
            break;
          case 'collectRefundPaymentMethod':
            response = await this._stripeTerminalApi.cancelCollectRefundPaymentMethod();
            break;
          case 'collectSetupIntentPaymentMethod':
            response = await this._stripeTerminalApi.cancelCollectSetupIntentPaymentMethod();
            break;
          case 'readReusableCard':
            response = await this._stripeTerminalApi.cancelReadReusableCard();
            break;
          default:
            throw new Error('No active collect method to cancel');
        }
      } catch (cause) {
        const error = new Error('Unable to cancel the collect method', { cause });
        error.code = UnhandledStripeServiceError.service_exception.code;
        throw error;
      }
    } else {
      try {
        response = await this._stripeTerminalApi.cancelCollectPaymentMethod();
      } catch (err) {
        throw {
          code: UnhandledStripeServiceError.service_exception.code,
          message: 'Unable to cancel the collect payment method',
        };
      }
    }
    return this._handleStripeResponse(response);
  }

  /**
   * Processes a payment after a payment method has been collected - Step 4
   *
   *
   * This method takes a single parameter, a PaymentIntent object obtained
   * from a successful call to collectPaymentMethod.
   *
   * @param {object} paymentIntent object that tracks the state of the payment through
   * the status attribute. When the payment is successful, the status of the PaymentIntent
   * changes to succeeded and you can confidently fulfill the order.
   *
   * @returns  a Promise that resolves to an object with the following fields:
   * @return {object} paymentIntent: The confirmed PaymentIntent object, if the command succeeded.
   * @return {error} error: An error, if the command failed. For more information, see Handling processing failures.
   */
  async processPaymentIntent(paymentIntent) {
    let response;
    try {
      response = await this._stripeTerminalApi.processPayment(paymentIntent);
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to process the payment intent',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * Begins collecting a payment method for an Interac Refund - Step 3.a
   *
   *
   * This method takes two arguments, the original chargeId
   * and the amount to be refunded on Interac.
   *
   * @param {string} chargeId: Original chargeId extracted from the refunded payment
   * @param {number} amount: Amount in cents you want to refund
   * this value should smaller or equal to the amount contained in the
   * original charge. This amount should be absolute.
   * @param {boolean} reverseTransfer tells Stripe which account to pull the funds from
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {} a empty promise resolution
   * @return {error} a promise rejection with an error message
   */

  async collectRefundPaymentMethod(chargeId, amount, reverseTransfer = true) {
    // For more details on the following method please see:
    // https://stripe.com/docs/terminal/js-api-reference#stripeterminal-collectrefundpaymentmethod
    let response;
    try {
      this.currentCollectMethod = 'collectRefundPaymentMethod';
      response = await this._stripeTerminalApi.collectRefundPaymentMethod(
        chargeId,
        amount,
        this.currency.toLowerCase(),
        {
          reverse_transfer: reverseTransfer,
          refund_application_fee: false,
        }
      );
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to collect the refund method',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * Processes a refund after a payment refund method has been collected - Step 4.a
   *
   *
   * This method takes no parameter and needs to be executed
   * right after a successful call to collectRefundPaymentMethod.
   *
   * @returns  a Promise that resolves to an object with the following fields:
   * @return {} a empty promise resolution
   * @return {error} a promise rejection with an error message
   */

  async processRefund() {
    let response;
    try {
      response = await this._stripeTerminalApi.processRefund();
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to process the refund intent',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * Begins collecting a payment method for an unreferenced card refund
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {} a empty promise resolution
   * @return {error} a promise rejection with an error message
   */
  async collectUnreferencedRefundPaymentMethod() {
    let response;
    try {
      this.currentCollectMethod = 'readReusableCard';
      response = await this._stripeTerminalApi.readReusableCard();
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to collect the refund method',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * Begins collecting a payment method for a setup intent.
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {} a empty promise resolution
   * @return {error} a promise rejection with an error message
   */
  async collectSetupIntentPaymentMethod(
    clientSecret,
    customerConsent = true,
    allowRedisplay = 'limited'
  ) {
    let response;
    try {
      // Stripe SDK accepts mixed type for the second parameter
      // In this case, it can be either boolean or 'always' | 'limited' | 'unspecified'
      this.currentCollectMethod = 'collectSetupIntentPaymentMethod';
      if (await hasFeature('PaymentsReplaceSetupIntentParameter')) {
        response = await this._stripeTerminalApi.collectSetupIntentPaymentMethod(
          clientSecret,
          allowRedisplay
        );
      } else {
        response = await this._stripeTerminalApi.collectSetupIntentPaymentMethod(
          clientSecret,
          customerConsent
        );
      }
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to collect the payment method',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * Confirms a setup intent after the payment method has been collected.
   *
   * @returns a Promise that resolves to an object with the following fields:
   * @return {} a empty promise resolution
   * @return {error} a promise rejection with an error message
   */
  async confirmSetupIntent(setupIntent) {
    let response;
    try {
      response = await this._stripeTerminalApi.confirmSetupIntent(setupIntent);
    } catch (err) {
      throw {
        code: UnhandledStripeServiceError.service_exception.code,
        message: 'Unable to confirm the payment method',
      };
    }

    return this._handleStripeResponse(response);
  }

  /**
   * This little handler make sure to return an error
   * or the response since Stripe always returns a
   * successful promise
   *
   * @param {Object} response
   */
  _handleStripeResponse(response) {
    const { error } = response;

    // If the response contains an error we throw back the error
    if (error) {
      // The following will resolve the error code with 'unknown_code' unless specified in the error

      throw {
        ...UnhandledStripeServiceError[error.code ?? UnhandledStripeServiceError.unknown_code.code],
        ...error,
      };
    }

    return response;
  }

  /**
   * Fetches a Connection token
   *
   * @return {Promise<string>} a connection token secret.
   */
  _fetchConnectionToken() {
    switch (get(this.gatewayConfig, 'attributes.parameters.platformVersion', '')) {
      case GatewayConfig.PLATFORM_VERSIONS.v3:
        return this._fetchV3ConnectionToken();

      default:
        return this._fetchV1ConnectionToken();
    }
  }

  /**
   * Fetches a Connection from the V1 Platform
   *
   * @return {Promise<string>} a connection token secret.
   */
  async _fetchV1ConnectionToken() {
    try {
      // The following call needs to be performed by onFetchConnectionToken
      // since this method is called every time we do initiate the Stripe SDK
      // which will refresh JWT token expiration timer. The JWT expires every 10 min.

      const { data: { jwt: bearerToken, url: urlToken } = {} } = await axios.post(CTS_TOKEN_URL);

      const url = new URL(
        `/payments/stripe-terminal-token/${this.supportedCountryCode.iso3}`,
        urlToken
      );

      const { data: { secret: secretToken } = {} } = await axios.request({
        headers: { Authorization: `Bearer ${bearerToken}` },
        method: 'POST',
        url,
      });

      return secretToken;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches a Connection from the V3 Platform
   *
   * @return {Promise<string>} a connection token secret.
   */
  async _fetchV3ConnectionToken() {
    // The following call needs to be performed by onFetchConnectionToken
    // since this method is called every time we do initiate the Stripe SDK
    // which will refresh JWT token expiration timer. The JWT expires every 10 min.
    try {
      const { data: { jwt, url } = {} } = await axios.post('/admin/payments/pay-service-jwt');

      const { data: { secret } = {} } = await axios.request({
        baseURL: url,
        url: `/stripe/gateway-configs/${this.gatewayConfig.id}/connection-token`,
        headers: { Authorization: `Bearer ${jwt}` },
        method: 'POST',
      });

      return secret || '';
    } catch (error) {
      this._terminalMonitoringInstance?.setAttributes({
        connectionTokenError: {
          message: error?.message,
          stack: error?.stack,
          code: error?.code,
        },
      });
      throw error;
    }
  }

  /**
   * Creates a Setup Intent for the purpose of capturing a card
   *
   * @return {Promise<SetupIntent>} Setup Intent
   */
  async createSetupIntent() {
    try {
      const { data: { jwt: bearerToken, url: urlToken } = {} } = await axios.post(
        '/admin/payments/pay-service-jwt'
      );

      const url = new URL(
        `/stripe/gateway-configs/${this.gatewayConfig.id}/setup-intent`,
        urlToken
      );

      const { data } = await axios.request({
        headers: { Authorization: `Bearer ${bearerToken}` },
        method: 'POST',
        url,
      });

      return get(data, 'data.attributes.secret', '');
    } catch (err) {
      throw err;
    }
  }
}
